import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

// Real service names from financial/banking domain
const SERVICE_NAMES = [
  'payment-processor', 'notification-service', 'user-identity', 'data-warehouse', 'analytics-engine',
  'card-management', 'loan-origination', 'deposit-service', 'investment-platform', 'mobile-gateway',
  'api-gateway', 'cache-service', 'message-queue', 'search-service', 'reporting-engine',
  'compliance-monitor', 'audit-logger', 'transaction-ledger', 'settlement-engine', 'risk-analyzer',
  'document-storage', 'imaging-service', 'crm-service', 'workflow-engine', 'scheduler-service',
  'notification-hub', 'email-service', 'sms-gateway', 'push-notification', 'webhook-dispatcher',
  'rate-limiter', 'circuit-breaker', 'service-registry', 'config-server', 'feature-flags',
  'telemetry-service', 'logging-service', 'metrics-collector', 'trace-aggregator', 'alert-manager',
  'backup-service', 'disaster-recovery', 'replication-service', 'sync-engine', 'migration-tool',
  'security-scanner', 'penetration-tester', 'vulnerability-tracker', 'secret-manager', 'encryption-service',
  'authentication-broker', 'authorization-service', 'session-manager', 'token-service', 'mfa-provider',
  'customer-portal', 'admin-dashboard', 'reporting-dashboard', 'monitoring-dashboard', 'analytics-dashboard',
  'batch-processor', 'job-scheduler', 'task-manager', 'workflow-orchestrator', 'event-processor',
  'subscription-service', 'billing-engine', 'invoicing-service', 'payment-gateway', 'revenue-recognizer',
  'inventory-service', 'supply-chain', 'procurement-system', 'warehouse-management', 'logistics-service',
  'recommendation-engine', 'personalization-service', 'content-delivery', 'media-service', 'cdn-manager',
  'machine-learning-pipeline', 'model-serving', 'feature-store', 'experiment-platform', 'data-labeling',
  'fraud-detection', 'anomaly-detection', 'pattern-recognition', 'prediction-service', 'forecast-engine',
  'compliance-checker', 'policy-engine', 'audit-trail', 'regulatory-reporter', 'sanctions-checker',
  'vendor-management', 'partner-portal', 'integration-hub', 'ecosystem-connector', 'marketplace-platform'
];

// Real team names from different organizational domains
const TEAM_NAMES = [
  'Retail Banking', 'Platform Engineering', 'Enterprise Solutions', 'Digital Innovation', 'Cloud Infrastructure',
  'Data Analytics', 'Security Operations', 'DevOps & Release', 'Frontend Engineering', 'Backend Services',
  'Mobile Development', 'Product Management', 'UX & Design', 'Quality Assurance', 'Technical Architecture',
  'Solutions Engineering', 'Customer Success', 'Operations & Support', 'Finance Technology', 'Risk Management',
  'Compliance & Legal', 'Human Resources', 'Business Development', 'Marketing Technology', 'Sales Engineering',
  'IT Infrastructure', 'Database Administration', 'Network Operations', 'Systems Engineering', 'Site Reliability',
  'Machine Learning Platform', 'Data Engineering', 'Analytics Engineering', 'Business Intelligence', 'AI Research',
  'API Platform', 'Integration Services', 'Message Broker Team', 'Cache & Session', 'Search & Indexing',
  'Authentication', 'Authorization & IAM', 'Encryption & Secrets', 'Monitoring & Logging', 'Observability',
  'Performance Engineering', 'Chaos Engineering', 'Disaster Recovery', 'Backup & Archival', 'Capacity Planning',
  'Vendor Management', 'Third-party Integration', 'Partner Enablement', 'Ecosystem Growth', 'Developer Relations',
  'Documentation & Training', 'Knowledge Management', 'Internal Tools', 'Automation Framework', 'Testing Infrastructure',
  'Security Scanning', 'Vulnerability Management', 'Penetration Testing', 'Code Review', 'Threat Intelligence',
  'Incident Response', 'On-call Management', 'Runbook Automation', 'Postmortem Analysis', 'Escalation Handling',
  'Cost Optimization', 'Resource Management', 'Budget Planning', 'Billing & Metering', 'Cost Allocation',
  'Change Management', 'Release Management', 'Deployment Pipeline', 'Rollback & Recovery', 'Feature Flags',
  'Compliance Automation', 'Audit Logging', 'Regulatory Reporting', 'Policy Enforcement', 'Governance',
  'Customer Data Privacy', 'Data Retention', 'Right to Deletion', 'Data Encryption', 'PII Protection',
  'Mobile Platform', 'iOS Development', 'Android Development', 'React Native', 'Cross-platform Support',
  'Web Platform', 'React Framework', 'Vue Development', 'Angular Team', 'Frontend Tools',
  'Microservices', 'Event-driven Architecture', 'Service Mesh', 'Container Orchestration', 'API Gateway',
  'Database Systems', 'SQL Optimization', 'NoSQL Strategy', 'Data Replication', 'Indexing Strategy',
  'Caching Strategy', 'Session Management', 'State Management', 'Distributed Transactions', 'Consistency',
  'Load Balancing', 'Traffic Management', 'Rate Limiting', 'Circuit Breaking', 'Resilience Patterns',
  'Batch Processing', 'Stream Processing', 'ETL Pipelines', 'Data Warehouse', 'Data Lake',
  'Real-time Analytics', 'Dashboard Development', 'Reporting Tools', 'BI Platform', 'Data Visualization',
  'Recommendation Engine', 'Personalization', 'Collaborative Filtering', 'A/B Testing', 'Experimentation',
  'Content Delivery', 'CDN Management', 'Static Assets', 'Media Processing', 'Video Streaming',
  'Email Services', 'SMS Gateway', 'Push Notifications', 'Webhook Dispatcher', 'Event Streaming',
  'Payment Processing', 'Transaction Management', 'Settlement', 'Reconciliation', 'Fraud Detection',
  'Account Management', 'Customer Onboarding', 'Profile Management', 'Preferences', 'Segmentation',
  'Loyalty Program', 'Rewards System', 'Points Management', 'Redemption', 'Partner Rewards',
  'Financial Reporting', 'Tax Compliance', 'Audit Trail', 'Regulatory Reporting', 'Risk Analysis',
  'Lending Platform', 'Credit Scoring', 'Application Processing', 'Approval Workflow', 'Servicing',
  'Investment Management', 'Portfolio Analysis', 'Asset Allocation', 'Risk Assessment', 'Performance Tracking',
  'Insurance Platform', 'Claims Processing', 'Policy Management', 'Underwriting', 'Risk Pricing',
  'Blockchain Integration', 'Smart Contracts', 'Distributed Ledger', 'Tokenization', 'Cryptocurrency',
  'IoT Platform', 'Sensor Integration', 'Device Management', 'Edge Computing', 'Time-series Data',
  'Quantum Computing', 'Algorithm Research', 'Simulation', 'Optimization', 'Cryptography',
  'Voice & Chat', 'NLP Processing', 'Intent Recognition', 'Dialog Management', 'Sentiment Analysis',
  'Computer Vision', 'Image Processing', 'Object Detection', 'OCR Services', 'Face Recognition',
  'Augmented Reality', 'Virtual Reality', 'Spatial Computing', '3D Rendering', 'Immersive Experiences'
];

// Mock data for development
// These are sample entities; real database has 1,606 entities with proper counts
const MOCK_ENTITIES = [
  {
    id: 'svc-payment',
    name: 'payment-service',
    entity_type: 'SERVICE',
    description: 'Core payment processing for ACH, wire transfers, and card transactions. Handles real-time payment validation and fraud detection.',
    source_url: 'https://docs.internal/services/payment',
    metadata: {
      team: 'Retail Banking',
      criticality: 'critical',
      version: '2.1.0',
      language: 'Java',
      status: 'Production',
      uptime_sla: '99.95%',
      team_lead: 'John Smith',
      on_call_team: 'Payments On-Call',
      documentation: 'https://wiki.internal/payment-service',
      repository: 'git@github.com:company/payment-service.git',
      deployment_frequency: 'Multiple times per week',
      support_hours: '24/7',
      data_classification: 'Confidential - PCI Sensitive'
    }
  },
  {
    id: 'svc-auth',
    name: 'auth-service',
    entity_type: 'SERVICE',
    description: 'Central authentication and authorization service. Manages OAuth 2.0 flows, JWT tokens, MFA, and session management.',
    source_url: 'https://docs.internal/services/auth',
    metadata: {
      team: 'Platform Engineering',
      criticality: 'critical',
      version: '3.0.1',
      language: 'Go',
      status: 'Production',
      uptime_sla: '99.99%',
      team_lead: 'Alice Johnson',
      on_call_team: 'Platform On-Call',
      documentation: 'https://wiki.internal/auth-service',
      repository: 'git@github.com:company/auth-service.git',
      deployment_frequency: 'Weekly',
      support_hours: '24/7',
      data_classification: 'Confidential',
      tech_stack: 'Go, PostgreSQL, Redis'
    }
  },
  {
    id: 'svc-loans',
    name: 'loans-service',
    entity_type: 'SERVICE',
    description: 'Auto loan origination and servicing. Handles applications, credit decisions, and payment scheduling.',
    source_url: 'https://docs.internal/services/loans',
    metadata: {
      team: 'Auto Finance',
      criticality: 'high',
      version: '1.8.3',
      language: 'Python',
      status: 'Production',
      uptime_sla: '99.9%',
      team_lead: 'Bob Wilson',
      on_call_team: 'Loans On-Call',
      documentation: 'https://wiki.internal/loans-service',
      repository: 'git@github.com:company/loans-service.git',
      deployment_frequency: 'Bi-weekly',
      support_hours: 'Business Hours',
      data_classification: 'Confidential - PII'
    }
  },
  {
    id: 'svc-accounts',
    name: 'accounts-service',
    entity_type: 'SERVICE',
    description: 'Customer account management including savings, checking, and CD accounts.',
    source_url: 'https://docs.internal/services/accounts',
    metadata: {
      team: 'Core Banking',
      criticality: 'critical',
      version: '5.2.1',
      language: 'Java',
      status: 'Production',
      uptime_sla: '99.95%',
      team_lead: 'Carol Davis',
      on_call_team: 'Banking On-Call',
      documentation: 'https://wiki.internal/accounts-service',
      repository: 'git@github.com:company/accounts-service.git',
      deployment_frequency: 'Multiple times per week',
      support_hours: '24/7',
      data_classification: 'Confidential - Financial Data'
    }
  },
  {
    id: 'svc-fraud',
    name: 'fraud-detection-service',
    entity_type: 'SERVICE',
    description: 'Real-time fraud detection using ML models. Analyzes transaction patterns and behavioral anomalies.',
    source_url: 'https://docs.internal/services/fraud-detection',
    metadata: {
      team: 'Risk Management',
      criticality: 'critical',
      version: '1.5.0',
      language: 'Python/Scala',
      status: 'Production',
      uptime_sla: '99.9%',
      team_lead: 'David Martinez',
      on_call_team: 'Risk On-Call',
      documentation: 'https://wiki.internal/fraud-detection',
      repository: 'git@github.com:company/fraud-detection.git',
      deployment_frequency: 'Weekly',
      support_hours: '24/7',
      data_classification: 'Confidential - Behavioral Data',
      ml_models: 'Random Forest, Neural Networks, Isolation Forest'
    }
  },
  ...Array.from({ length: 214 }, (_, i) => {
    const serviceName = SERVICE_NAMES[i % SERVICE_NAMES.length];
    return {
      id: `svc-${i+6}`,
      name: serviceName,
      entity_type: 'SERVICE',
      description: `Microservice handling ${serviceName.replace(/-/g, ' ')} functionality`
    };
  }),
  { id: 'team-retail', name: 'Retail Banking', entity_type: 'TEAM', description: 'Responsible for consumer banking products and services.', metadata: { members: 24, lead: 'Sarah Chen' } },
  { id: 'team-platform', name: 'Platform Engineering', entity_type: 'TEAM', description: 'Core platform infrastructure and shared services.', metadata: { members: 18, lead: 'Mike Johnson' } },
  ...Array.from({ length: 98 }, (_, i) => {
    const teamName = TEAM_NAMES[(i + 2) % TEAM_NAMES.length];
    return {
      id: `team-${i+3}`,
      name: teamName,
      entity_type: 'TEAM',
      description: `${teamName} - Dedicated team for ${teamName.toLowerCase()} initiatives`
    };
  }),
  { id: 'pattern-oauth2', name: 'OAuth 2.0 + PKCE', entity_type: 'PATTERN', description: 'Standard OAuth 2.0 authorization code flow with PKCE for public clients.', metadata: { category: 'Authentication' } },
  { id: 'pattern-circuit', name: 'Circuit Breaker', entity_type: 'PATTERN', description: 'Prevents cascade failures by tracking failure rates and opening circuit when threshold exceeded.', metadata: { category: 'Resilience' } },
  ...Array.from({ length: 24 }, (_, i) => ({ id: `pattern-${i+3}`, name: `Pattern ${i+3}`, entity_type: 'PATTERN', description: `Pattern ${i+3}` })),
  { id: 'api-1', name: 'Payment API', entity_type: 'API', description: 'Payment processing API', metadata: { version: '1.0' } },
  ...Array.from({ length: 87 }, (_, i) => ({ id: `api-${i+2}`, name: `API ${i+2}`, entity_type: 'API', description: `API ${i+2}` })),
  { id: 'reg-pci', name: 'PCI-DSS', entity_type: 'REGULATION', description: 'Payment Card Industry Data Security Standard. Requirements for handling cardholder data.', metadata: { version: '4.0' } },
  ...Array.from({ length: 9 }, (_, i) => ({ id: `reg-${i+2}`, name: `Regulation ${i+2}`, entity_type: 'REGULATION', description: `Regulation ${i+2}` })),
];

const MOCK_RELATIONSHIPS = [
  { source_id: 'svc-payment', target_id: 'svc-auth', rel_type: 'DEPENDS_ON', description: 'Authentication for payment authorization' },
  { source_id: 'svc-payment', target_id: 'svc-fraud', rel_type: 'DEPENDS_ON', description: 'Real-time fraud check' },
  { source_id: 'svc-payment', target_id: 'svc-accounts', rel_type: 'DEPENDS_ON', description: 'Balance verification' },
  { source_id: 'svc-loans', target_id: 'svc-auth', rel_type: 'DEPENDS_ON', description: 'User authentication' },
  { source_id: 'svc-loans', target_id: 'svc-payment', rel_type: 'DEPENDS_ON', description: 'Loan disbursement' },
  { source_id: 'svc-payment', target_id: 'team-retail', rel_type: 'OWNED_BY', description: 'Primary ownership' },
  { source_id: 'svc-auth', target_id: 'team-platform', rel_type: 'OWNED_BY', description: 'Primary ownership' },
  { source_id: 'svc-auth', target_id: 'pattern-oauth2', rel_type: 'USES_PATTERN', description: 'OAuth implementation' },
  { source_id: 'svc-payment', target_id: 'pattern-circuit', rel_type: 'USES_PATTERN', description: 'External service protection' },
  { source_id: 'svc-payment', target_id: 'reg-pci', rel_type: 'COMPLIES_WITH', description: 'Card data handling' },
  { source_id: 'svc-accounts', target_id: 'team-retail', rel_type: 'OWNED_BY', description: 'Account services ownership' },
  { source_id: 'svc-fraud', target_id: 'svc-payment', rel_type: 'INTEGRATES_WITH', description: 'Fraud scoring integration' },
  // Add more realistic relationships for large-scale organization
  ...Array.from({ length: 200 }, (_, i) => {
    const serviceNum = (i % 100) + 6;
    const teamNum = (i % 50) + 3;
    return {
      source_id: `svc-${serviceNum}`,
      target_id: `team-${teamNum}`,
      rel_type: 'OWNED_BY',
      description: `Service owned by team`
    };
  }),
  ...Array.from({ length: 300 }, (_, i) => {
    const sourceService = (i % 80) + 6;
    const targetService = ((i + 13) % 80) + 6;
    const depTypes = ['DEPENDS_ON', 'INTEGRATES_WITH', 'CALLS', 'PUBLISHES_TO'];
    return {
      source_id: `svc-${sourceService}`,
      target_id: `svc-${targetService}`,
      rel_type: depTypes[i % depTypes.length],
      description: `Service-to-service dependency`
    };
  }),
  ...Array.from({ length: 100 }, (_, i) => {
    const serviceNum = (i % 50) + 6;
    const patternNum = (i % 26) + 3;
    return {
      source_id: `svc-${serviceNum}`,
      target_id: `pattern-${patternNum}`,
      rel_type: 'USES_PATTERN',
      description: `Service uses design pattern`
    };
  }),
];

export function useEntities() {
  const [entities, setEntities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useMock, setUseMock] = useState(false); // Use real API

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch real data from API
      const [entitiesResponse, statsResponse, relationshipsResponse] = await Promise.all([
        api.getEntities(),
        api.getStatistics(),
        api.getRelationships(),
      ]);

      // Extract entities from response
      const entitiesData = entitiesResponse?.entities || [];
      // Store stats from API
      const statsData = statsResponse?.statistics || null;
      // Extract relationships from response
      const relationshipsData = relationshipsResponse?.relationships || [];

      setEntities(entitiesData);
      setStats(statsData);
      setRelationships(relationshipsData);
    } catch (err) {
      console.warn('Error loading entities:', err.message);
      // Only fall back to mock data if API fails AND useMock is true
      if (useMock) {
        setEntities(MOCK_ENTITIES);
        setRelationships(MOCK_RELATIONSHIPS);
      } else {
        setEntities([]);
        setRelationships([]);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [useMock]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const getEntityById = useCallback((id) => {
    return entities.find(e => e.id === id);
  }, [entities]);

  const getEntitiesByType = useCallback((type) => {
    return entities.filter(e => e.entity_type === type);
  }, [entities]);

  const getRelationshipsForEntity = useCallback((entityId) => {
    const outgoing = relationships.filter(r => r.source_id === entityId);
    const incoming = relationships.filter(r => r.target_id === entityId);
    return { outgoing, incoming };
  }, [relationships]);

  const getStatistics = useCallback(() => {
    // Use stats from API if available, otherwise count from entities
    if (stats) {
      return stats;
    }
    return {
      service: entities.filter(e => e.entity_type === 'SERVICE').length,
      team: entities.filter(e => e.entity_type === 'TEAM').length,
      pattern: entities.filter(e => e.entity_type === 'PATTERN').length,
      api: entities.filter(e => e.entity_type === 'API').length,
      regulation: entities.filter(e => e.entity_type === 'REGULATION').length,
      relationships: relationships.length,
    };
  }, [entities, relationships, stats]);

  return {
    entities,
    relationships,
    loading,
    error,
    refetch: fetchEntities,
    getEntityById,
    getEntitiesByType,
    getRelationshipsForEntity,
    getStatistics,
  };
}

export function useEntity(entityId) {
  const [entity, setEntity] = useState(null);
  const [relationships, setRelationships] = useState({ outgoing: [], incoming: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!entityId) {
      setEntity(null);
      setLoading(false);
      return;
    }

    const fetchEntity = async () => {
      setLoading(true);
      try {
        // Fetch real data from API
        const entity = await api.getEntity(entityId);
        const entityRelationships = await api.getEntityRelationships(entityId);

        setEntity(entity || null);
        setRelationships(entityRelationships || { outgoing: [], incoming: [] });
      } catch (err) {
        setError(err.message);
        // Fall back to mock data if available
        const mockEntity = MOCK_ENTITIES.find(e => e.id === entityId);
        const mockOutgoing = MOCK_RELATIONSHIPS.filter(r => r.source_id === entityId);
        const mockIncoming = MOCK_RELATIONSHIPS.filter(r => r.target_id === entityId);

        setEntity(mockEntity || null);
        setRelationships({ outgoing: mockOutgoing, incoming: mockIncoming });
      } finally {
        setLoading(false);
      }
    };

    fetchEntity();
  }, [entityId]);

  return { entity, relationships, loading, error };
}
